import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import DialogBox from '../../../components/shared/DialogBox';
import Message from '../../../components/shared/Message';
import { Alert, Fade } from 'react-bootstrap';
import { connect } from 'react-redux';
import { InlineEdit } from '../../../components/shared/InlineEdit';
import { fetchBrokerages } from '../../actions';
import {
    cancelInvitation,
    clearError,
    disableUser,
    enableUser,
    fetchUsers,
    inviteUser,
    resendInvitation,
    revokeUser,
    saveUser,
    changeBrokerage,
    EditUser,
    InviteUser,
    PendingInviteList,
    UserList,
    InviteFailureModal
} from '../../../users';
import {
    fetch as fetchInvites,
    remove as removeInvite
} from './../../../invites/actions';
import { removeAlert } from '../../../alerts';
import { history } from '../../../store';
import _some from 'lodash/some';
import isEmpty from 'lodash/isEmpty';
import { updateBrokerage } from '../../api'

import routes from '../../../constants/routes'

function canManageBrokerages(user) {
    if (!user || !user.permissions) return false;
    return _some([
        user.permissions.includes('brokers:read'),
        user.permissions.includes('brokers:write')
    ]);
}

export function BrokerageDetail(props) {
    const { brokerageId } = useParams();

    const [brokerage, setBrokerage] = useState(null);
    const [commissionInput, setCommissionInput] = useState('N/A');
    const [existingUser, setExistingUser] = useState(null);
    const [existingBrokerage, setExistingBrokerage] = useState(null);
    const [invitation, setInvitation] = useState({});
    const [selectedUser, setSelectedUser] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showInviteFailureModal, setShowInviteFailureModal] = useState(false);
    const [filteredUsers, setFilteredUsers] = useState([]);

    useEffect(() => {
        if (!canManageBrokerages(props.user)) {
            history.push(routes.BROKERAGES);
        } else {
            props.fetchBrokerages(props.user, true);
            props.fetchUsers(props.user);
            props.fetchInvites({ brokerId: brokerageId });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!props.brokerages) return;
        const found = props.brokerages.find(item => item.brokerId === brokerageId);
        setBrokerage(found);
        setCommissionInput(found ? found.commission : 'N/A');
    }, [props.brokerages, brokerageId]);

    useEffect(() => {
        if (!props.users) return;

        const { invitation: inv } = props.users;
        setInvitation(inv);

        const users = props.users.items.filter(
            item => item.brokerId === brokerageId && item.status !== 'pending'
        );
        setFilteredUsers(users);

        if (!isEmpty(props.users.error)) {
            const { errorCode, errorData = {} } = props.users.error;
            const errorCodeType = ['InviteExistsBrokerError', 'UserExistBrokerError'];

            if (errorCodeType.includes(errorCode)) {
                if (props.brokerages) {
                    setExistingBrokerage(
                        props.brokerages.find(item => item.brokerId === errorData.brokerId)
                    );
                }
                const found = props.users.items.find(item => item.email === inv.email) ||
                    { email: inv.email, isActive: true };
                setExistingUser(found);
                setShowInviteFailureModal(true);
            } else {
                setShowInviteFailureModal(false);
                setExistingUser(null);
                setExistingBrokerage(null);
            }
        } else {
            setShowInviteFailureModal(false);
            setExistingUser(null);
            setExistingBrokerage(null);
        }
    }, [props.users, props.brokerages, brokerageId]);

    const handleBrokerageChangeCancel = useCallback(() => {
        setSelectedUser(null);
        setShowEditModal(false);
        setShowInviteFailureModal(false);
        if (props.clearError) props.clearError();
    }, [props.clearError]);

    const handleCancelInvitation = useCallback((record) => {
        props.removeInvite(record);
    }, [props.removeInvite]);

    const handleDisableUser = useCallback((user) => {
        props.disableUser(user, props.user);
    }, [props.disableUser, props.user]);

    const handleDismissAlert = useCallback((alert) => {
        if (props.removeAlert) props.removeAlert(alert);
    }, [props.removeAlert]);

    const handleEditUser = useCallback((user) => {
        setSelectedUser(user);
        setShowEditModal(true);
    }, []);

    const handleEditUserCancel = useCallback(() => {
        setSelectedUser(null);
        setShowEditModal(false);
    }, []);

    const handleEditUserSave = useCallback((user) => {
        props.saveUser(user, props.user);
        setSelectedUser(null);
        setShowEditModal(false);
    }, [props.saveUser, props.user]);

    const handleBrokerageChange = useCallback((user, newBrokerage) => {
        props.changeBrokerage(user, newBrokerage, props.user);
        setSelectedUser(null);
        setShowEditModal(false);
        setShowInviteFailureModal(false);
    }, [props.changeBrokerage, props.user]);

    const handleEnableUser = useCallback((user) => {
        props.enableUser(user, props.user);
    }, [props.enableUser, props.user]);

    const handleInviteUserSubmit = useCallback((inv) => {
        const { email, isAdmin, type } = inv;
        setInvitation(inv);
        props.inviteUser(email, isAdmin, type, brokerageId, props.user);
    }, [props.inviteUser, props.user, brokerageId]);

    const handleResendInvitation = useCallback((user) => {
        props.resendInvitation(user, props.user);
    }, [props.resendInvitation, props.user]);

    const handleRevokeUser = useCallback((user) => {
        props.revokeUser(user, props.user);
    }, [props.revokeUser, props.user]);

    const handleCommissionSave = useCallback(async (value) => {
        setCommissionInput(value.commissionInput);
        const updated = { ...brokerage, commission: value.commissionInput };
        await updateBrokerage(updated.brokerId, updated, props.user);
    }, [brokerage, props.user]);

    const validateCommission = (value) => {
        return value && !isNaN(value) && (Number(value) >= 0 && Number(value) <= 100);
    };

    return (
        <div className="homepageContainer">
            <div className="container">
                <div className="brokerage routeContainer">
                    {props.alerts.map((alert, index) => (
                        <Fade key={index} in={true} timeout={5000}>
                            <Alert bsStyle={alert.type} onDismiss={() => handleDismissAlert(alert)}>
                                <Message message={alert.message} linkId={alert.linkId} linkName={alert.linkName} />
                            </Alert>
                        </Fade>
                    ))}
                    <div>
                        <h6 className="agency-code-header">
                            <span className="uppercase">agency #</span>
                            <span>{brokerage ? brokerage.agencyCode : ''}</span>
                        </h6>
                    </div>
                    <div>
                        <h3 className="agency-name-header">
                            {brokerage ? brokerage.name : ''}
                        </h3>
                    </div>
                    <div>
                        <h5 className="agency-state-header">
                            {brokerage ? brokerage.state : ''}
                        </h5>
                    </div>
                    <div className="agency-detail clearfix">
                        <div className="agency-detail-column pull-left">
                            <div className="title gray">address</div>
                            <div>{brokerage ? brokerage.address1 : ''}</div>
                            <div>{brokerage ? brokerage.address2 : ''}</div>
                            <div>
                                {brokerage && brokerage.city && (
                                    <span>{brokerage.city}</span>
                                )}
                                {brokerage && brokerage.city && brokerage.state && <span>, </span>}
                                {brokerage && brokerage.state && (
                                    <span>{brokerage.state} </span>
                                )}
                                {brokerage && brokerage.zip && (
                                    <span>{brokerage.zip}</span>
                                )}
                            </div>
                        </div>
                        <div className="agency-detail-column pull-left">
                            <div className="title gray">commission</div>
                            <div className="inline-edit-commission">
                                <InlineEdit
                                    value={commissionInput}
                                    change={handleCommissionSave}
                                    propName="commissionInput"
                                    classEditing="entity-edit"
                                    classInvalid="entity-invalid"
                                    validate={validateCommission}
                                />%
                            </div>
                        </div>
                    </div>
                    <div className="user-management-container clearfix">
                        <div>
                            <div className="invite-user-container col-lg-4 col-md-4 col-sm-10 col-xs-12">
                                <InviteUser
                                    adminSelectorVisibility={'visible'}
                                    invitation={invitation}
                                    isBusy={props.users.isBusy}
                                    data={{
                                        brokerageName: brokerage ? brokerage.name : '',
                                        brokerageStatus: brokerage?.status
                                    }}
                                    onSubmit={handleInviteUserSubmit}
                                />
                            </div>
                            <div className="pending-users-container col-lg-8 col-md-8 col-sm-10 col-xs-12">
                                <PendingInviteList
                                    invites={props.invites}
                                    onResendInvitation={handleResendInvitation}
                                    onCancelInvitation={handleCancelInvitation}
                                    disableResend={brokerage?.status === 'disabled'}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="users-container clearfix">
                        <UserList
                            onDisableUser={handleDisableUser}
                            onEditUser={handleEditUser}
                            onEnableUser={handleEnableUser}
                            onRevokeUser={handleRevokeUser}
                            user={props.user}
                            users={filteredUsers}
                        />
                    </div>
                </div>
                <DialogBox
                    title="Edit User"
                    custom_class="editModal"
                    show={showEditModal}
                    onHide={handleEditUserCancel}
                >
                    {selectedUser && (
                        <EditUser
                            user={selectedUser}
                            brokerages={props.brokerages}
                            currentBrokerage={brokerage}
                            adminSelectorDisabled={selectedUser.id === props.user.id}
                            onCancel={handleEditUserCancel}
                            onSave={handleEditUserSave}
                            onBrokerageChange={handleBrokerageChange}
                        />
                    )}
                </DialogBox>
                <DialogBox
                    title="Cannot send invite"
                    custom_class="editModal"
                    show={showInviteFailureModal}
                    onHide={handleBrokerageChangeCancel}
                >
                    <InviteFailureModal
                        onSave={handleBrokerageChange}
                        user={existingUser}
                        newBrokerage={brokerage}
                        existingBrokerage={existingBrokerage}
                        brokerages={props.brokerages}
                        onCancel={handleBrokerageChangeCancel}
                    />
                </DialogBox>
            </div>
        </div>
    );
}

BrokerageDetail.propTypes = {
    alerts: PropTypes.array.isRequired,
    brokerages: PropTypes.array.isRequired,
    user: PropTypes.object.isRequired,
    users: PropTypes.object.isRequired,
    changeBrokerage: PropTypes.func.isRequired,
    clearError: PropTypes.func.isRequired,
    disableUser: PropTypes.func.isRequired,
    enableUser: PropTypes.func.isRequired,
    fetchBrokerages: PropTypes.func.isRequired,
    fetchUsers: PropTypes.func.isRequired,
    invites: PropTypes.array,
    inviteUser: PropTypes.func.isRequired,
    removeAlert: PropTypes.func.isRequired,
    resendInvitation: PropTypes.func.isRequired,
    revokeUser: PropTypes.func.isRequired,
    saveUser: PropTypes.func.isRequired
};

const mapStateToProps = store => ({
    alerts: store.alerts.items,
    brokerages: store.brokerages.items,
    invites: store.invites.items,
    user: store.user,
    users: store.users
});

export default connect(mapStateToProps, {
    cancelInvitation,
    changeBrokerage,
    clearError,
    disableUser,
    enableUser,
    fetchBrokerages,
    fetchInvites,
    fetchUsers,
    inviteUser,
    removeAlert,
    removeInvite,
    resendInvitation,
    revokeUser,
    saveUser
})(BrokerageDetail);